/**
 * analytics.ts — thin, provider-agnostic event tracker.
 *
 * SUB-1 Phase 3B lands the first real event catalog (subscription
 * lifecycle + usage limits per spec §29). We're intentionally NOT
 * wiring PostHog / Amplitude / Segment yet — Rennan hasn't picked a
 * provider and shipping one blindly locks a $10-15k/yr decision on
 * the wrong foot.
 *
 * Contract:
 *   track('event_name', { key: 'value', … })
 *
 * Never log payment card data, full JWTs, or raw Stripe/Apple tokens.
 * `props` should be short, snake_case keys; numbers, strings, booleans.
 *
 * When the provider decision lands, swap the console.info line for
 * `posthog.capture(…)` / `analytics.track(…)` — every call site keeps
 * working because they all go through `track()`.
 *
 * FREEWEEK-1 (Oct 5) — events now also land in Orbit's own
 * `funnel_events` table through `log_funnel_event()`. 88 people on the
 * waitlist got their invites and nobody signed up, and there was no way
 * to tell where they stopped. Only for a visitor who accepted analytics
 * in the cookie banner — the cookie policy promises exactly that — and
 * with no account attached: a random id this browser keeps
 * (`orbit_anon_id`, set only after that consent) makes one visitor's
 * steps count as one person. No third party. Fire and forget — a
 * tracker never slows or breaks a page. Mission Control → Early access
 * reads the totals, beside the counts from Orbit's own records, which
 * don't depend on anyone's browser.
 */

import { supabase } from '@/integrations/supabase/client';
import { getStoredConsent } from '@/components/shared/CookieConsentBanner';
import { debug } from '@/lib/debug';

/** Union of every event name we currently emit — helps catch typos. */
export type AnalyticsEvent =
  // Subscription lifecycle (spec §29)
  | 'subscription_screen_viewed'
  | 'plan_selected'
  | 'purchase_started'
  | 'purchase_completed'
  | 'purchase_failed'
  | 'purchase_canceled'
  | 'purchase_restored'
  | 'subscription_upgraded'
  | 'subscription_downgraded'
  | 'subscription_expired'
  // Usage limit events (spec §29 + PLANS-1B additions)
  | 'storage_limit_reached'
  | 'storage_upgrade_viewed'
  | 'team_limit_reached'
  | 'client_limit_reached'
  | 'project_limit_reached'
  // Cross-provider guardrail (spec §22)
  | 'cross_provider_blocked'
  // BIZ-WAITLIST-1 — the Business (video plan) waitlist on the pricing sheet
  | 'business_waitlist_submitted'
  // FREEWEEK-1 / OPEN-1 — the way in, from the marketing page to a plan
  | 'landing_viewed'
  | 'early_access_viewed'
  | 'waitlist_joined'
  | 'auth_viewed'
  | 'invite_opened'
  | 'invite_invalid'
  | 'account_created'
  | 'signed_in'
  | 'founding_page_viewed'
  | 'founding_cta_clicked'
  | 'setup_step'
  | 'setup_finished'
  | 'today_viewed'
  | 'plan_screen_viewed'
  | 'checkout_started'
  | 'checkout_returned';

export type AnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Fire an analytics event. Recorded in Orbit's own `funnel_events`
 * (FREEWEEK-1); in dev + preview builds the payload is also logged so
 * the event catalog can be checked in the console.
 */
export function track(event: AnalyticsEvent, props?: AnalyticsProps): void {
  // Guard against `undefined` values slipping into the payload so a
  // downstream provider (PostHog etc.) doesn't reject the request.
  const safeProps: AnalyticsProps = {};
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value !== undefined) safeProps[key] = value;
    }
  }

  // Dev/preview visibility. debug.log auto-strips in prod builds.
  debug.log(`[analytics] ${event}`, safeProps);

  // FREEWEEK-1 — Orbit's own record (see the header), only with the
  // visitor's say-so. Never awaited, never thrown: the page carries on
  // whatever happens to the report.
  if (typeof window === 'undefined') return;
  if (getStoredConsent()?.analytics !== true) {
    forgetAnonId();
    return;
  }
  try {
    void (supabase as any)
      .rpc('log_funnel_event', {
        p_event: event,
        p_props: safeProps,
        p_anon_id: anonId(),
        p_path: window.location.pathname,
      })
      .then(
        () => undefined,
        () => undefined,
      );
  } catch {
    /* no client yet — nothing to record */
  }
}

const ANON_ID_KEY = 'orbit_anon_id';

/** Consent withdrawn (or never given): the id goes too. */
function forgetAnonId(): void {
  try {
    if (window.localStorage.getItem(ANON_ID_KEY) !== null) window.localStorage.removeItem(ANON_ID_KEY);
  } catch {
    /* storage blocked — nothing kept to forget */
  }
}

/** A random id for this browser, kept (with analytics consent) so one visitor counts once. */
function anonId(): string | null {
  try {
    let id = window.localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      window.localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Convenience helper for the plan-change delta events. Compares an
 * old plan code to a new one and emits `subscription_upgraded` /
 * `subscription_downgraded` accordingly. No-ops if the tier didn't
 * actually move (e.g. billing interval flipped monthly ↔ yearly).
 */
export function trackPlanChange(
  from: string | null | undefined,
  to: string | null | undefined,
  extra?: AnalyticsProps,
): void {
  if (!from || !to || from === to) return;
  // PLANS-1: three paid tiers. Legacy `orbit_pro` rank kept as Orbit-
  // equivalent so mid-migration transitions still classify correctly.
  const rank: Record<string, number> = {
    free: 0,
    orbit_starter: 1,
    orbit: 2,
    orbit_pro: 2,
    orbit_business: 3,
  };
  const fromRank = rank[from] ?? 0;
  const toRank = rank[to] ?? 0;
  if (toRank > fromRank) {
    track('subscription_upgraded', { from, to, ...extra });
  } else if (toRank < fromRank) {
    track('subscription_downgraded', { from, to, ...extra });
  }
}
